import React from "react";
import { useTranslation } from "react-i18next";
import { Puzzle, Calendar, Mail } from "lucide-react";
import { Badge } from "../common";
import SectionShell, { FieldGroup, FieldGroups } from "./SectionShell";

/**
 * Outbound integrations.
 *
 * Only Gmail/Calendar sync lives here, and it is scaffolding — it is explicitly
 * out of scope in the plan and needs Google Cloud OAuth credentials that no
 * instance has yet. Kept visible rather than hidden so an admin looking for it
 * finds a clear "not available" instead of concluding the feature is somewhere
 * they haven't looked.
 *
 * Google *sign-in* is unrelated and is not configured here: it is env-driven
 * (`GOOGLE_CLIENT_ID` + `GOOGLE_WORKSPACE_DOMAIN`), and the account-picker hint
 * that goes with it is a branding field. Saying so out loud in the copy is
 * worth it — the two have been confused before.
 */

const PLANNED = [
  { icon: Mail,     titleKey: "settings.integrationGmail",    descKey: "settings.integrationGmailDesc" },
  { icon: Calendar, titleKey: "settings.integrationCalendar", descKey: "settings.integrationCalendarDesc" },
];

export default function IntegrationSettings() {
  const { t } = useTranslation();

  return (
    <SectionShell
      icon={Puzzle} tone="primary"
      title={t("settings.integrations")} desc={t("settings.integrationsDesc")}>

      <FieldGroups>
        <FieldGroup label={t("settings.google")} hint={t("settings.googleDesc")}>
          <ul className="space-y-2.5">
            {PLANNED.map(({ icon: Icon, titleKey, descKey }) => (
              <li key={titleKey}
                className="flex items-start gap-3 rounded-sm border border-border bg-surface px-3.5 py-3">
                <Icon size={16} className="text-muted shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t(titleKey)}</p>
                  <p className="text-xs text-muted mt-0.5">{t(descKey)}</p>
                </div>
                <Badge value="lost" label={t("settings.notConfigured")} />
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted">{t("settings.integrationsRequires")}</p>
        </FieldGroup>

        <FieldGroup label={t("settings.integrationSignInGroup")}>
          <p className="text-sm text-muted max-w-prose">{t("settings.integrationSignInNote")}</p>
        </FieldGroup>
      </FieldGroups>
    </SectionShell>
  );
}
